import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  User, Mail, Phone, MapPin, FileText, Check, X, 
  Microscope, Pill, FlaskConical, Download, Edit2, ArrowLeft 
} from 'lucide-react';
import { Patient, DischargeSummary } from '../lib/mock-data';
import { EmptyState } from './common/EmptyState';

interface ClinicalSummaryTabProps {
  patient: Patient;
  dischargeSummary: DischargeSummary;
  clinicalNotesData : any;
  onBack?: () => void;
  userRole?: 'doctor' | 'physiotherapist' | 'coordinator' | 'nurse';
}

export function ClinicalSummaryTab({ patient, dischargeSummary, clinicalNotesData, onBack, userRole }: ClinicalSummaryTabProps) {
  // Mock diagnosis data
  const diagnoses = [
    { diagnosis: 'DIAGNOSIS', notes: '' },
    { diagnosis: 'CONDITION AT THE TIME OF DISCHARGE', notes: '' }
  ];

  const snakeToCamelSpace = (text:string) => {
    if (!text) return "";
    return text
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Mock doctor's notes for diagnosis
  const doctorNotes = ``;

  return (
    <div className="space-y-4">
      {/* Diagnosis Section */}
      <Card className="shadow-lg overflow-hidden">
        <CardHeader className="bg-primary text-white pb-3">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Diagnosis
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {/* Diagnosis List */}
          <div className="space-y-3">
              <div className="p-3 bg-secondary rounded-lg border-l-4 border-primary">
                <p className="text-foreground font-medium mb-1">DIAGNOSIS</p>
                <p className="text-muted-foreground text-sm  whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin   whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin  ">{dischargeSummary.diagnosis?.diagnosis}</p>
              </div>
          </div>
          <div className="space-y-3">
              <div className="p-3 bg-secondary rounded-lg border-l-4 border-primary">
                <p className="text-foreground font-medium mb-1">CONDITION AT THE TIME OF DISCHARGE</p>
                <p className="text-muted-foreground text-sm  whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin   whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin ">{dischargeSummary.diagnosis?.conditionAtTheTimeOfDischarge}</p>
              </div>
          </div>
          
          {/* Doctor's Notes */}
          <div className="p-4 bg-[#FEEBDA] rounded-lg border border-[#F07D13]/20">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-[#F07D13]" />
              <p className="text-[#494949] font-medium">Doctor's Notes</p>
            </div>
            <p className="text-foreground text-sm leading-relaxed">
              {dischargeSummary.diagnosis?.doctor_notes.treatment}<br/>
              {dischargeSummary.diagnosis?.doctor_notes.chief_complaint}<br/>
              {dischargeSummary.diagnosis?.doctor_notes.present_history}
            </p>
          </div>
        </CardContent>
      </Card>

      {dischargeSummary ? (
        <>
          {/* 2. Clinical Summary / Discharge Information */}
          <Card className="shadow-lg overflow-hidden">
            <CardHeader className="bg-primary text-white pb-3">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Clinical Summary - Discharge Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Diagnosis & Transplant */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-secondary rounded-lg">
                  <p className="text-[#494949] text-xs mb-1">Diagnosis</p>
                  <p className="text-foreground text-sm">{dischargeSummary.clinicalSummary?.diagnosis}</p>
                </div>
                <div className="p-3 bg-secondary rounded-lg">
                  <p className="text-[#494949] text-xs mb-1">Transplant Date</p>
                  <p className="text-foreground text-sm">
                    {dischargeSummary.clinicalSummary?.transplantDate}
                  </p>
                </div>
              </div>

              {/* Post-Transplant Events */}
              <div className="border border-border rounded-lg p-3">
                <h4 className="text-primary mb-3 text-sm">Post-Transplant Events</h4>
                <div className="">
                    {clinicalNotesData?.notes && clinicalNotesData.notes.length > 0 ? (
                      clinicalNotesData.notes.map((note, index) => (
                        <div
                          key={note.id}
                          className={`p-4 rounded-xl mt-4 ${
                            index !== clinicalNotesData.notes.length - 1 ? "border-b" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-primary">Dr. {note.doctor_name}</p>
                            <div className="flex items-center gap-2">
                              <p className="text-muted-foreground text-sm">
                                {new Date(note.created_at).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true
                                }).replace(/am|pm/gi, match => match.toUpperCase())}
                              </p>                              
                            </div>
                          </div>
                          <div className="space-y-3 pl-4 pr-4">
                            <div>
                              <p className="text-foreground whitespace-pre-wrap">
                                <span className="text-xs text-muted-foreground">Clinical Observations  </span>
                                {note.notes}
                              </p>
                            </div>
                            
                            {(note.airway_complications || note.rejection_events) && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-border">
                                {note.airway_complications && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Airway Complications</p>
                                    <Badge variant="outline" className="bg-[#F07D13]/10 border-[#F07D13]/30 text-[#F07D13]">
                                      {note.airway_complications}
                                    </Badge>
                                  </div>
                                )}
                                
                                {note.rejection_events && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Rejection Events</p>
                                    <Badge variant="outline" className="bg-destructive/10 border-destructive/30 text-destructive">
                                      {note.rejection_events}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <EmptyState
                        title="No clinical notes yet"
                        description="Add your first clinical note above to start tracking patient progress."
                        icon={FileText}
                      />
                    )}
                </div>
              </div>
              
              <div className="space-y-3">
                  <div className="p-3 bg-secondary rounded-lg border-l-4 border-primary">
                    <p className="text-foreground font-medium mb-1">COURSE IN THE HOSPITAL</p>
                    <p className="text-muted-foreground text-sm  whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin   whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin ">{dischargeSummary.clinicalSummary?.courseInHospital}</p>
                  </div>
              </div>

              <div className="space-y-3">
                  <div className="p-3 bg-secondary rounded-lg border-l-4 border-primary">
                    <p className="text-foreground font-medium mb-1">CONDITION AT THE TIME OF DISCHARGE</p>
                    <p className="text-muted-foreground text-sm  whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin   whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin ">{dischargeSummary.clinicalSummary?.conditionAtDischarge}</p>
                  </div>
              </div>
            </CardContent>
          </Card>

          {/* 5. Laboratory Values Card */}
          <Card className="shadow-lg overflow-hidden">
            <CardHeader className="bg-primary text-white pb-3">
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5" />
                Review and Lab Values
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3 mb-4">
                  <div className="p-3 bg-secondary rounded-lg border-l-4 border-primary">
                    <p className="text-foreground font-medium mb-1">ENDOCRINOLOGY REVIEW</p>
                    <p className="text-muted-foreground text-sm  whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin   whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin ">{dischargeSummary.endocrinologyReview}</p>
                  </div>
              </div>
              <div className="space-y-3 mb-4">
                  <div className="p-3 bg-secondary rounded-lg border-l-4 border-primary">
                    <p className="text-foreground font-medium mb-1">OTHER INSTRUCTIONS</p>
                    <p className="text-muted-foreground text-sm  whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin   whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin  ">{dischargeSummary.otherInstructions}</p>
                  </div>
              </div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="p-3 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[#494949] text-xs">Tacrolimus Level</p>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                      {dischargeSummary.labValues.date_time}
                    </Badge>
                  </div>
                  <p className="text-foreground text-sm">Level : {dischargeSummary.labValues.level}</p>
                  <p className="text-[#494949] text-xs mt-1">Notes: {dischargeSummary.labValues.dosage_note}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Removed: Doctor's Remarks & Counseling section as per requirements */}

          {/* 7. Actions / Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-white rounded-lg shadow-lg border border-border">
            <p className="text-muted-foreground text-sm  whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin   whitespace-pre-line max-h-64 overflow-y-auto pr-2 scrollbar-thin ">
              Last updated on {new Date(dischargeSummary.enteredDate).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
            <div className="flex gap-3">
              {onBack && (
                <Button variant="default" className="bg-accent hover:bg-accent/90" onClick={onBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to List
                </Button>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* No discharge summary message */}
          <Card className="shadow-lg">
            <CardContent className="p-12">
              <div className="text-center">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-foreground text-lg mb-2">No Clinical Summary Available</h3>
                <p className="text-muted-foreground mb-6">
                  Discharge summary has not been completed for this patient yet.
                </p>
                <Button className="bg-accent hover:bg-accent/90">
                  <FileText className="w-4 h-4 mr-2" />
                  Create Discharge Summary
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}